/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { getApiUrl } from "@/utils/baseUrl";

// Mirrors internal/agent/detail.go (SystemDetail). Every section except host,
// cpu and failed_units is optional: the agent omits what the host cannot report.

export interface DetailHost {
  hostname: string;
  platform: string;
  platform_version: string;
  kernel: string;
  arch: string;
  virtualization?: string;
  uptime_seconds: number;
  boot_time: number;
  processes: number;
}

export interface DetailCPU {
  per_core: number[];
  total: number;
  load1: number;
  load5: number;
  load15: number;
  iowait: number;
  steal: number;
  system: number;
  user: number;
  threads: number;
}

export interface PSIWindow {
  avg10: number;
  avg60: number;
  avg300: number;
}

export interface DetailPressure {
  cpu_some: PSIWindow;
  mem_some: PSIWindow;
  mem_full: PSIWindow;
  io_some: PSIWindow;
  io_full: PSIWindow;
}

export interface DetailDiskIO {
  name: string;
  read_bytes: number;
  write_bytes: number;
  read_count: number;
  write_count: number;
  io_time_ms: number;
  weighted_io_ms: number;
}

export interface DetailNIC {
  name: string;
  up: boolean;
  speed_mbps?: number;
  mac?: string;
  addrs?: string[];
  rx_bytes: number;
  tx_bytes: number;
  rx_packets: number;
  tx_packets: number;
  rx_errors: number;
  tx_errors: number;
  rx_dropped: number;
  tx_dropped: number;
}

export interface DetailSMART {
  device: string;
  model?: string;
  serial?: string;
  health: "passed" | "failed" | "unknown" | "standby";
  temp_c?: number;
  power_on_hours?: number;
  power_cycles?: number;
  reallocated?: number;
  pending?: number;
  uncorrectable?: number;
  crc_errors?: number;
  wear_percent_used?: number;
  capacity_bytes?: number;
  rotational: boolean;
  protocol?: string;
}

export interface DetailZFSVdev {
  name: string;
  state: string;
  read: string;
  write: string;
  cksum: string;
  depth: number;
}

export interface DetailZFSPool {
  name: string;
  health: string;
  size_bytes: number;
  alloc_bytes: number;
  free_bytes: number;
  frag_percent: number;
  cap_percent: number;
  scan?: string;
  status?: string;
  action?: string;
  errors?: string;
  devices?: DetailZFSVdev[];
}

export interface DetailUPS {
  name: string;
  model?: string;
  status: string;
  status_text: string;
  charge_percent: number;
  runtime_seconds: number;
  load_percent: number;
  input_voltage: number;
  output_voltage: number;
  battery_voltage: number;
  output_frequency: number;
  power_nominal?: number;
}

export interface DetailGuest {
  id: string;
  vmid: number;
  name: string;
  type: string;
  status: string;
  cpu: number;
  maxcpu: number;
  mem: number;
  maxmem: number;
  disk: number;
  maxdisk: number;
  uptime: number;
  netin: number;
  netout: number;
  pool?: string;
  template: boolean;
}

export interface DetailDocker {
  name: string;
  image: string;
  state: string;
  status: string;
  cpu_percent: number;
  mem_bytes: number;
  mem_percent: number;
}

export interface DetailUnit {
  unit: string;
  active: string;
  sub: string;
  description?: string;
}

export interface DetailProcess {
  pid: number;
  name: string;
  user?: string;
  cpu_percent: number;
  mem_bytes: number;
  mem_percent: number;
}

export interface SystemDetail {
  collected_at: string;
  host: DetailHost;
  cpu: DetailCPU;
  pressure?: DetailPressure;
  disk_io?: DetailDiskIO[];
  nics?: DetailNIC[];
  smart?: DetailSMART[];
  zfs?: DetailZFSPool[];
  ups?: DetailUPS;
  guests?: DetailGuest[];
  docker?: DetailDocker[];
  failed_units: DetailUnit[];
  processes?: DetailProcess[];
  power: { enabled: boolean };
}

export interface AgentPowerInfo {
  reachable: boolean;
  agent: { enabled: boolean; hostname: string; actions: string[] } | null;
  wake: { available: boolean; router?: string; macs?: number };
}

export type PowerAction = "reboot" | "shutdown";

async function readError(response: Response, fallback: string): Promise<Error> {
  const data = await response.json().catch(() => ({}));
  return new Error(data.error || fallback);
}

export async function getMonitorAgentDetail(id: number): Promise<SystemDetail> {
  const response = await fetch(getApiUrl(`/monitor/agents/${id}/detail`));
  if (!response.ok) throw await readError(response, "Failed to fetch server detail");
  return response.json();
}

export async function getMonitorAgentPower(id: number): Promise<AgentPowerInfo> {
  const response = await fetch(getApiUrl(`/monitor/agents/${id}/power`));
  if (!response.ok) throw await readError(response, "Failed to fetch power info");
  return response.json();
}

export async function postMonitorAgentPower(
  id: number,
  action: PowerAction,
  confirm: string,
): Promise<void> {
  const response = await fetch(getApiUrl(`/monitor/agents/${id}/power`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, confirm }),
  });
  if (!response.ok) throw await readError(response, `Failed to ${action}`);
}

export async function postMonitorAgentWake(id: number): Promise<void> {
  const response = await fetch(getApiUrl(`/monitor/agents/${id}/wake`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
  if (!response.ok) throw await readError(response, "Failed to send wake-on-LAN");
}
